import { isYouTube, ytVideoId } from './youtube.js';
import { createYtAudioEngine } from './yt-audio-engine.js';

// window.GLZ.player — the site audio player backing the header live button and the strip player
// (app-player-new). Replaces the Angular Redux player store + player-new.component engine.
//
// It manages a playlist (live programme or fetched tracks), a shared HTMLAudioElement, and
// publishes rich state via onState(cb) so the strip reflects the current/next item, play state,
// elapsed time and seekability. Track playback info comes from the existing PlayerV2 endpoint
// (/umbraco/api/playerv2/playlist?ids=…), unchanged. (YouTube/videojs track URLs — rare — are a
// follow-up; live + MP3 tracks, the common case, are handled here.)
export function installPlayerFacade() {
  window.GLZ = window.GLZ || {};
  if (window.GLZ.player) return;

  const PLAYLIST_URL = '/umbraco/api/playerv2/playlist';

  let audio = null;
  let liveUrl = '';
  let playlist = [];   // [{ id, title, desc, image, url, durationInSec, link, isLive }]
  let liveSegments = [];   // last known [current, next] live items, preserved across track playback
  let index = 0;
  let kind = null;     // 'live' | 'tracks'
  let volume = 50;
  let yt = null;   // lazily-created YouTube audio engine (createYtAudioEngine)
  const listeners = new Set();

  const current = () => playlist[index] || null;
  const nextItem = () => playlist[index + 1] || null;
  const bust = u => (u ? u + (u.indexOf('?') === -1 ? '?' : '&') + '_=' + Date.now() : u);

  const state = () => {
    const c = current();
    const n = nextItem();
    const isYt = engineKindFor(c) === 'yt';
    const ytDur = isYt && yt ? yt.getDuration() : 0;
    return {
      kind,
      playing: isYt ? (!!yt && yt.isPlaying()) : (!!audio && !audio.paused && !audio.ended),
      live: kind === 'live',
      current: c ? { id: c.id, title: c.title || '', desc: c.desc || '', image: c.image || '', isLive: !!c.isLive, link: c.link || '' } : null,
      next: n ? { title: n.title || '', desc: n.desc || '', image: n.image || '', link: n.link || '' } : null,
      hasNext: !!n && playlist.length > 1,
      currentTime: isYt ? (yt ? yt.getCurrentTime() : 0) : (audio ? audio.currentTime || 0 : 0),
      duration: isYt ? ytDur : (audio && isFinite(audio.duration) ? audio.duration : 0),
      seekable: isYt ? (!!c && !c.isLive && ytDur > 0) : (!!c && !c.isLive && !!audio && isFinite(audio.duration) && audio.duration > 0)
    };
  };

  const emit = () => { const s = state(); listeners.forEach(cb => { try { cb(s); } catch (e) { /* one bad subscriber must not break others */ } }); };

  const onEnded = () => { if (nextItem()) { index++; playCurrent(true); } else emit(); };

  // live + direct-audio → <audio>; YouTube tracks → YT engine
  const engineKindFor = item => (item && !item.isLive && isYouTube(item.url)) ? 'yt' : 'audio';

  const ensureYt = () => {
    if (!yt) yt = createYtAudioEngine({ onChange: emit, onEnded });
    return yt;
  };

  const ensureAudio = () => {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    ['play', 'playing', 'pause', 'waiting', 'stalled', 'error', 'timeupdate', 'loadedmetadata', 'durationchange'].forEach(ev => audio.addEventListener(ev, emit));
    audio.addEventListener('ended', onEnded);
    audio.volume = volume / 100;
    return audio;
  };

  // A live stream resumes at the live edge (reload + cache-bust); a track resumes its buffer.
  function playCurrent(reload) {
    const c = current();
    if (!c) return;
    if (engineKindFor(c) === 'yt') {
      if (audio) audio.pause();
      ensureYt().play(ytVideoId(c.url));
      emit();
      return;
    }
    if (yt) yt.pause();
    const a = ensureAudio();
    const url = c.isLive ? bust(c.url || liveUrl) : c.url;
    if (!url) return;
    if (reload || !a.src) { a.src = url; a.load(); }
    const p = a.play();
    if (p && typeof p.catch === 'function') p.catch(() => emit());
    emit();
  }

  async function fetchItems(ids) {
    try {
      const res = await fetch(`${PLAYLIST_URL}?ids=${encodeURIComponent(ids)}`);
      if (!res.ok) return [];
      const items = await res.json();
      // normalise PlayerV2 PlaylistItem (PascalCase) to lower-case keys
      return (items || []).map(i => ({
        id: i.id ?? i.Id, title: i.title ?? i.Title, desc: i.desc ?? i.Desc,
        image: i.image ?? i.Image, url: i.url ?? i.Url,
        durationInSec: i.durationInSec ?? i.DurationInSec, link: i.link ?? i.Link, isLive: i.isLive ?? i.IsLive
      }));
    } catch (e) { console.error('[player] fetch failed', e); return []; }
  }

  window.GLZ.player = {
    setLiveUrl(url) { if (url) liveUrl = url; },

    // Seed the strip's initial display (current + next live programme) from server-rendered data.
    initLive(items) { if (items && items.length) { liveSegments = items; playlist = items; index = 0; kind = 'live'; emit(); } },

    // Advance the live display to a new current/next pair, driven by live-schedule.js. Only the
    // display changes — the stream URL and playback are untouched, so a swap never interrupts audio.
    // While the user is playing a track OR a GLGLZ digital station, the pair is remembered but not
    // applied. A station keeps kind === 'live' (it IS live), but its playlist item's id is the
    // station's GUID, not 'live'/'live-next' — without the second guard below, a boundary would
    // overwrite the strip with the main channel's song/image while the station keeps streaming.
    // Either way, returning to live (playLive) restores the real programme from the remembered pair
    // instead of a blank item.
    setLiveNow(cur, nxt) {
      const items = [];
      if (cur) items.push({ id: 'live', title: cur.title || '', desc: cur.desc || '', image: cur.image || '', link: cur.link || '', url: liveUrl, isLive: true });
      if (nxt) items.push({ id: 'live-next', title: nxt.title || '', desc: nxt.desc || '', image: nxt.image || '', link: nxt.link || '', isLive: true });
      if (!items.length) return;

      liveSegments = items;
      const c = current();
      if (kind === 'tracks' || (c && c.id !== 'live' && c.id !== 'live-next')) return;

      playlist = items;
      index = 0;
      if (kind === null) kind = 'live';
      emit();
    },

    async playTrack(id) {
      const items = await fetchItems(id);
      if (!items.length) return;
      playlist = items; index = 0; kind = items[0].isLive ? 'live' : 'tracks';
      playCurrent(true);
    },

    // GLGLZ digital station: a live streaming channel resolved by GUID + home rootId via
    // /playerv2/GetDigitalStation → { channelUrl, xmlUrl, image, id }. Mirrors the legacy
    // PlayDigitalStation effect: play channelUrl as a live item. Clicking the current
    // station again toggles pause, matching the old digital-stations.component play().
    async playStation(id, rootId, meta) {
      const c = current();
      if (c && String(c.id) === String(id) && state().playing) { this.pause(); return; }
      try {
        const res = await fetch(`/umbraco/api/playerv2/GetDigitalStation?id=${encodeURIComponent(id)}&rootId=${encodeURIComponent(rootId)}`);
        if (!res.ok) return;
        const s = await res.json();
        const url = s.channelUrl ?? s.ChannelUrl;
        if (!url) return;
        playlist = [{
          id: s.id ?? s.Id ?? id,
          title: (meta && meta.title) || '',
          desc: (meta && meta.desc) || '',
          image: s.image ?? s.Image ?? '',
          url,
          isLive: true
        }];
        index = 0; kind = 'live';
        playCurrent(true);
      } catch (e) { console.error('[player] station failed', e); }
    },

    async addToQueue(id) {
      const items = await fetchItems(id);
      if (!items.length) return;
      const wasEmpty = playlist.filter(i => !i.isLive).length === 0;
      if (kind !== 'tracks') { playlist = items; index = 0; kind = 'tracks'; }
      else playlist = playlist.concat(items);
      if (wasEmpty) playCurrent(true); else emit();
    },

    // Header live button: switch to live + play; toggles off if already playing live.
    playLive() {
      const s = state();
      if (s.live && s.playing) { this.pause(); return; }
      if (kind !== 'live' || !current() || !current().isLive) {
        const seed = liveSegments.length ? liveSegments
          : [{ id: 'live', title: '', desc: '', image: '', url: liveUrl, isLive: true }];
        playlist = seed;
        index = 0; kind = 'live';
      }
      playCurrent(true);
    },

    // Strip play/pause button: resume/pause the current item (track or live).
    togglePlay() {
      if (state().playing) { this.pause(); return; }
      if (current()) playCurrent(false); else this.playLive();
    },

    pause() { if (audio) audio.pause(); if (yt) yt.pause(); emit(); },
    next() { if (nextItem()) { index++; playCurrent(true); } },
    seek(percent) {
      if (engineKindFor(current()) === 'yt') {
        if (yt) { yt.seekTo(yt.getDuration() * percent / 100); emit(); }
        return;
      }
      if (audio && isFinite(audio.duration)) { audio.currentTime = audio.duration * percent / 100; emit(); }
    },
    setVolume(v) { volume = Math.max(0, Math.min(100, Number(v) || 0)); if (audio) audio.volume = volume / 100; if (yt) yt.setVolume(volume); },
    isPlayingLive() { return state().live && state().playing; },

    onState(cb) { listeners.add(cb); cb(state()); return () => listeners.delete(cb); }
  };

  // Prime the hidden YT player up-front so the first tap plays within the gesture (mobile autoplay).
  const warmYt = () => { if (document.querySelector('[data-play-btn]')) ensureYt().warm(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', warmYt);
  else warmYt();
}
